/**
 * Feedback capture vocabulary + context assembly.
 *
 * `FeedbackCategory` mirrors the backend `Literal` in
 * web-demo-backend/src/feedback_notes.py — keep the two in sync (the backend
 * validates the value at the data layer, so a drift here surfaces as a 400).
 */

// Build info injected by Vite (declared in vite-env.d.ts). Combine version +
// short SHA to match the footer's "v1.2.1.<sha>" so a note pins the exact build.
const APP_VERSION = (() => {
  const version = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";
  const hash = typeof __BUILD_HASH__ !== "undefined" ? __BUILD_HASH__ : "";
  return hash ? `${version}.${hash}` : version;
})();

const MAX_TARGET_TEXT = 200;

export type FeedbackCategory = "ui_wording" | "model_behavior" | "workflow_bug" | "other";

export const FEEDBACK_CATEGORIES: ReadonlyArray<{
  value: FeedbackCategory;
  label: string;
}> = [
  { value: "ui_wording", label: "UI / wording" },
  { value: "model_behavior", label: "Model behavior" },
  { value: "workflow_bug", label: "Workflow / bug" },
  { value: "other", label: "Other" },
];

export interface CapturedTarget {
  tag: string;
  text: string;
  testId?: string;
  ariaLabel?: string;
  id?: string;
  rect: { x: number; y: number; width: number; height: number };
}

export interface ViewportSnapshot {
  slot: string;
  seriesId: string;
  sliceIndex: number;
  totalSlices: number;
  zoom: number;
}

/** Describe the element under the cursor so UI feedback is self-locating. */
export function captureTarget(node: EventTarget | null): CapturedTarget | undefined {
  if (!(node instanceof Element)) return undefined;
  const rect = node.getBoundingClientRect();
  return {
    tag: node.tagName.toLowerCase(),
    text: (node.textContent ?? "").trim().slice(0, MAX_TARGET_TEXT),
    testId: node.getAttribute("data-testid") ?? undefined,
    ariaLabel: node.getAttribute("aria-label") ?? undefined,
    id: node.id || undefined,
    rect: {
      x: Math.round(rect.x),
      y: Math.round(rect.y),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    },
  };
}

/**
 * Assemble the reproduction context attached to a feedback note. Display-only
 * on the backend (stored as a JSON string), so the shape can evolve freely.
 */
export function buildContext(args: {
  target?: CapturedTarget;
  viewport?: ViewportSnapshot;
}): Record<string, unknown> {
  const context: Record<string, unknown> = {
    url: window.location.href,
    route: window.location.pathname,
    app_version: APP_VERSION,
    user_agent: navigator.userAgent,
  };
  if (args.target) context.element = args.target;
  if (args.viewport) context.viewport = args.viewport;
  return context;
}
